import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Send, Paperclip, Mic } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ----------------------------- Markdown ----------------------------- */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text ?? '';
    return div.innerHTML;
}

function renderMarkdown(text) {
    let html = escapeHtml(text || '');
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/^---$/gm, '<hr/>');
    html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`);
    html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    html = html.replace(/\n/g, '<br/>');
    html = html.replace(/<br\/?><(h[23]|ul|hr|li)/g, '<$1');
    html = html.replace(/<\/(h[23]|ul|hr|li)><br\/?>/g, '</$1>');
    return html;
}

/* ----------------------------- File helpers ----------------------------- */
async function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/* ============================ Component ============================
   Embedded inline chat panel. Always open. No launcher.
   Props:
     - className?: string (outer wrapper override)
     - height?: string (Tailwind/CSS height for the chat surface, default "h-[560px]")
     - testId?: string (data-testid for the root, e.g. "planner-home")
   ==================================================================== */
const AIPlanner = ({
    className = '',
    height = 'h-[560px]',
    testId = 'planner',
}) => {
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]); // { role, content, images? }
    const [input, setInput] = useState('');
    const [previews, setPreviews] = useState([]); // {dataUrl, base64, media_type}
    const [loading, setLoading] = useState(true); // true while starting + during turns
    const [listening, setListening] = useState(false);
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(true);

    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const recognitionRef = useRef(null);
    const planSavedRef = useRef(false);

    /* ---------------- Speech recognition setup ---------------- */
    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) return;
        const recognition = new SR();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-GB';
        recognition.onresult = (e) => {
            const transcript = e.results[0][0].transcript;
            setInput((cur) => (cur ? `${cur} ${transcript}` : transcript));
        };
        recognition.onerror = () => setListening(false);
        recognition.onend = () => setListening(false);
        recognitionRef.current = recognition;
    }, []);

    /* ---------------- Auto-scroll on new message ----------------
       IMPORTANT: only scroll the INNER messages container — never the
       page itself. Using el.scrollIntoView() would jump the whole
       window down to the chat on mount, which we don't want.
    -------------------------------------------------------------- */
    useEffect(() => {
        const end = messagesEndRef.current;
        if (!end) return;
        // Walk up to the nearest scrollable ancestor (overflow-y auto/scroll)
        let container = end.parentElement;
        while (container) {
            const style = window.getComputedStyle(container);
            if (/(auto|scroll)/.test(style.overflowY)) break;
            container = container.parentElement;
        }
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages, loading]);

    /* ---------------- Auto-start: kick off /planner/start once ----------------
       StrictMode-safe: an `active` flag discards stale responses from the
       synthetic double-mount in dev. Two backend sessions may be created in
       dev mode (harmless — each is independent and persists in MongoDB).
    -------------------------------------------------------------------------- */
    useEffect(() => {
        let active = true;
        axios
            .post(`${API}/planner/start`)
            .then((res) => {
                if (!active) return;
                setSessionId(res.data.session_id);
                setMessages([{ role: 'assistant', content: res.data.message }]);
            })
            .catch((err) => {
                if (!active) return;
                console.error(err);
                setError('Could not connect to the AI planner. Please try again.');
            })
            .finally(() => {
                if (!active) return;
                setStarting(false);
                setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    /* ---------------- Auto-save when AI emits a completed plan ---------------- */
    useEffect(() => {
        if (!sessionId || planSavedRef.current) return;
        const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
        if (!lastAssistant) return;
        const content = lastAssistant.content || '';
        if (/Website Plan for/i.test(content) && /Recommended Package/i.test(content)) {
            planSavedRef.current = true;
            axios.post(`${API}/planner/save`, { session_id: sessionId }).catch(() => {});
        }
    }, [messages, sessionId]);

    /* ---------------- File upload ---------------- */
    const handleFiles = async (e) => {
        const files = Array.from(e.target.files || []).slice(0, 4 - previews.length);
        const newPreviews = [...previews];
        for (const f of files) {
            try {
                const dataUrl = await fileToBase64(f);
                if (typeof dataUrl !== 'string' || !dataUrl.includes(',')) continue;
                const base64 = dataUrl.split(',')[1];
                if (!base64) continue;
                newPreviews.push({
                    dataUrl,
                    base64,
                    media_type: f.type || 'image/jpeg',
                });
            } catch (err) {
                console.error(err);
            }
        }
        setPreviews(newPreviews);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removePreview = (idx) => {
        setPreviews((p) => p.filter((_, i) => i !== idx));
    };

    /* ---------------- Send message ---------------- */
    const sendMessage = useCallback(async () => {
        const text = input.trim();
        if ((!text && previews.length === 0) || loading || !sessionId) return;

        const userImages = previews.map((p) => p.dataUrl);
        setMessages((m) => [
            ...m,
            { role: 'user', content: text || '(Uploaded reference images)', images: userImages },
        ]);
        const imagesToSend = previews.map((p) => ({
            data: p.base64,
            media_type: p.media_type,
        }));
        setInput('');
        setPreviews([]);
        setLoading(true);
        setError(null);

        try {
            const res = await axios.post(`${API}/planner/message`, {
                session_id: sessionId,
                text,
                images: imagesToSend,
            });
            setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
        } catch (err) {
            console.error(err);
            setMessages((m) => [
                ...m,
                {
                    role: 'assistant',
                    content: '⚠️ Something went wrong. Please try sending your message again.',
                },
            ]);
        } finally {
            setLoading(false);
        }
    }, [input, previews, loading, sessionId]);

    /* ---------------- Voice ---------------- */
    const toggleVoice = () => {
        const rec = recognitionRef.current;
        if (!rec) return;
        if (listening) {
            rec.stop();
            setListening(false);
        } else {
            try {
                rec.start();
                setListening(true);
            } catch {
                setListening(false);
            }
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const onInputChange = (e) => {
        setInput(e.target.value);
        const ta = e.target;
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    };

    const hasContent = input.trim().length > 0 || previews.length > 0;
    const canSend = hasContent && !loading && sessionId;
    const speechSupported =
        typeof window !== 'undefined' &&
        (window.SpeechRecognition || window.webkitSpeechRecognition);

    return (
        <div
            className={`flex flex-col w-full max-w-full bg-white rounded-2xl border border-[#E8E8EC] overflow-hidden shadow-soft ${height} ${className}`}
            data-testid={testId}
        >
            {/* ===== Header ===== */}
            <div className="relative px-5 py-4 bg-gradient-to-br from-[#0F0F1E] via-[#2D1B69] to-[#1a1145] text-white flex items-center gap-3.5 shrink-0">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#7B61FF] to-[#00E5CC] flex items-center justify-center font-bold text-lg shrink-0">
                    R
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px] tracking-tight">
                        REVOLO AI Planner
                    </div>
                    <div className="text-[11.5px] text-white/65 flex items-center gap-1.5 mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00E5CC] animate-pulse" />
                        {starting
                            ? 'Connecting…'
                            : sessionId
                            ? 'Online — Describe your dream website'
                            : 'Offline'}
                    </div>
                </div>
            </div>

            {/* ===== Messages ===== */}
            <div
                className="flex-1 overflow-y-auto px-4 pt-4 pb-2 bg-[#FAFAFD]"
                data-testid={`${testId}-messages`}
            >
                {starting && messages.length === 0 && (
                    <div className="flex items-center justify-center h-full">
                        <TypingIndicator label="Waking up the AI…" />
                    </div>
                )}
                {messages.map((m, i) => (
                    <MessageBubble key={i} message={m} />
                ))}
                {!starting && loading && <TypingIndicator />}
                {error && !sessionId && (
                    <div
                        className="m-2 p-4 rounded-xl bg-red-50 border border-red-200 text-[13px] text-red-700"
                        data-testid={`${testId}-error`}
                    >
                        {error}
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* ===== Preview chips ===== */}
            {previews.length > 0 && (
                <div
                    className="flex gap-2 px-4 py-2.5 bg-white border-t border-[#E8E8EC] flex-wrap shrink-0"
                    data-testid={`${testId}-previews`}
                >
                    {previews.map((p, i) => (
                        <div key={i} className="relative">
                            <img
                                src={p.dataUrl}
                                alt={`preview-${i}`}
                                className="w-14 h-14 rounded-xl object-cover border-2 border-[#E8E8EC]"
                            />
                            <button
                                type="button"
                                onClick={() => removePreview(i)}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#E24B4A] text-white text-[10px] border-2 border-white flex items-center justify-center hover:scale-110 transition-transform"
                                aria-label="Remove image"
                                data-testid={`${testId}-remove-preview-${i}`}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* ===== Input ===== */}
            <div className="px-4 py-3 bg-white border-t border-[#E8E8EC] shrink-0">
                <div className="flex items-end gap-2">
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!sessionId}
                        className="w-10 h-10 rounded-xl border-[1.5px] border-[#E8E8EC] bg-[#FAFAFD] text-[#6B6B80] hover:border-[#7B61FF] hover:bg-[#F0EDFF] hover:text-[#7B61FF] flex items-center justify-center transition-all shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Upload images"
                        data-testid={`${testId}-upload`}
                    >
                        <Paperclip className="w-[18px] h-[18px]" />
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFiles}
                        data-testid={`${testId}-file-input`}
                    />

                    {speechSupported && (
                        <button
                            type="button"
                            onClick={toggleVoice}
                            disabled={!sessionId}
                            className={`w-10 h-10 rounded-xl border-[1.5px] flex items-center justify-center transition-all shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                                listening
                                    ? 'border-[#7B61FF] bg-[#F0EDFF] text-[#7B61FF] animate-pulse'
                                    : 'border-[#E8E8EC] bg-[#FAFAFD] text-[#6B6B80] hover:border-[#7B61FF] hover:bg-[#F0EDFF] hover:text-[#7B61FF]'
                            }`}
                            aria-label="Voice input"
                            data-testid={`${testId}-voice`}
                        >
                            <Mic className="w-[18px] h-[18px]" />
                        </button>
                    )}

                    <textarea
                        ref={inputRef}
                        rows={1}
                        value={input}
                        onChange={onInputChange}
                        onKeyDown={onKeyDown}
                        placeholder={
                            !sessionId
                                ? 'Starting…'
                                : listening
                                ? 'Listening…'
                                : 'Describe your dream website…'
                        }
                        disabled={!sessionId}
                        className="flex-1 min-h-10 max-h-32 px-3.5 py-2.5 text-[14px] text-[#1A1A2E] bg-[#FAFAFD] border-[1.5px] border-[#E8E8EC] rounded-2xl outline-none resize-none focus:border-[#7B61FF] transition-colors placeholder:text-[#C8C8D4] disabled:opacity-60"
                        data-testid={`${testId}-input`}
                    />

                    <button
                        type="button"
                        onClick={sendMessage}
                        disabled={!canSend}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${
                            canSend
                                ? 'bg-gradient-to-br from-[#7B61FF] to-[#5B45CC] text-white shadow-[0_2px_12px_rgba(123,97,255,0.35)] hover:scale-105'
                                : 'bg-[#E8E8EC] text-[#C8C8D4] cursor-default'
                        }`}
                        aria-label="Send message"
                        data-testid={`${testId}-send`}
                    >
                        <Send className="w-[18px] h-[18px]" />
                    </button>
                </div>
                <div className="text-center mt-1.5 text-[10.5px] text-[#C8C8D4] tracking-wide">
                    Powered by REVOLO AI WEB
                </div>
            </div>
        </div>
    );
};

/* ============================ Sub-components ============================ */
const MessageBubble = ({ message }) => {
    const isUser = message.role === 'user';
    return (
        <div className={`flex mb-3 ${isUser ? 'justify-end' : ''}`}>
            {!isUser && (
                <div className="w-[30px] h-[30px] rounded-full bg-gradient-to-br from-[#7B61FF] to-[#00E5CC] flex items-center justify-center text-white text-[12px] font-bold mr-2.5 mt-0.5 shrink-0">
                    R
                </div>
            )}
            <div
                className={`max-w-[82%] px-4 py-2.5 text-[13.5px] leading-relaxed break-words whitespace-pre-wrap ${
                    isUser
                        ? 'bg-gradient-to-br from-[#7B61FF] to-[#5B45CC] text-white rounded-[18px_2px_18px_18px]'
                        : 'bg-white text-[#1A1A2E] border border-[#E8E8EC] rounded-[2px_18px_18px_18px] planner-ai-bubble'
                }`}
            >
                {message.images && message.images.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {message.images.map((src, idx) => (
                            <img
                                key={idx}
                                src={src}
                                alt={`upload-${idx}`}
                                className="w-[72px] h-[72px] object-cover rounded-[10px] border-2 border-white/30"
                            />
                        ))}
                    </div>
                )}
                {isUser ? (
                    <span>{message.content}</span>
                ) : (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(renderMarkdown(message.content), {
                                ALLOWED_TAGS: ['h2', 'h3', 'strong', 'em', 'ul', 'ol', 'li', 'hr', 'code', 'br', 'p'],
                                ALLOWED_ATTR: [],
                            }),
                        }}
                    />
                )}
            </div>
        </div>
    );
};

const TypingIndicator = ({ label = 'Thinking…' }) => (
    <div className="flex items-center gap-2.5 py-2 pl-1" data-testid="planner-typing">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#7B61FF] to-[#00E5CC] flex items-center justify-center text-white text-[10px] font-bold">
            R
        </div>
        <div className="flex gap-1">
            <span className="w-[7px] h-[7px] rounded-full bg-[#7B61FF] animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-[7px] h-[7px] rounded-full bg-[#7B61FF] animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-[7px] h-[7px] rounded-full bg-[#7B61FF] animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
        <span className="text-[11.5px] text-[#6B6B80]">{label}</span>
    </div>
);

export default AIPlanner;
